import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@parrot-software/design-system';

interface FormFieldHoverHelpProps {
  /** Icon component to display as the trigger */
  icon: React.ReactNode;
  /** Help text to display in the hover card */
  helpText: string;
  /** Width of the hover card (default: 80) */
  width?: number;
  /** Delay before opening the hover card in milliseconds (default: 600) */
  openDelay?: number;
  /** Delay before closing the hover card in milliseconds (default: 300) */
  closeDelay?: number;
}

/**
 * Form field hover help component
 *
 * Displays a help icon that shows additional information when hovered.
 * Used in form field labels to provide contextual help without cluttering the UI.
 *
 * @param {FormFieldHoverHelpProps} props - Component properties
 * @param {React.ReactNode} props.icon - Icon component to display as the trigger
 * @param {string} props.helpText - Help text to display in the hover card
 * @param {number} [props.width=80] - Width of the hover card
 * @param {number} [props.openDelay=600] - Delay before opening in milliseconds
 * @param {number} [props.closeDelay=300] - Delay before closing in milliseconds
 * @returns {React.ReactElement} Hover help component
 *
 * @example
 * <FormFieldHoverHelp
 *   icon={<CircleHelp className="size-4 text-blue-700" />}
 *   helpText="El rol determina los permisos del usuario"
 * />
 */
export const FormFieldHoverHelp = ({
  icon,
  helpText,
  width = 80,
  openDelay = 300,
  closeDelay = 300,
}: FormFieldHoverHelpProps): React.ReactElement => {
  return (
    <HoverCard closeDelay={closeDelay} openDelay={openDelay}>
      <HoverCardTrigger asChild>
        <span className="inline-flex cursor-help">{icon}</span>
      </HoverCardTrigger>
      <HoverCardContent align={'start'} className={`w-${width} r-0`} side={'bottom'}>
        <div className="space-y-2">
          <p className="text-sm">{helpText}</p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
